import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { cloneDeep } from 'lodash';
import deepmerge from 'deepmerge';
import {
  AddressType,
  type AreaPageListing,
  type MapPageCluster,
  type SingleAddress,
} from '@zoocasa/go-search';
import type { PartialDeep } from 'type-fest';
import type { Precision } from 'components/search-es/geo_helper';
import { AreaPageListingCardData } from 'components/pages/area-listings/area_page_listing_card_data';
import type { ListingCardData } from 'components/listing-card';
import {
  areMapBoundsUsableForSearch,
  calculateCenterPoint,
  getAreaClusterPrecisionFromZoom,
  getGeohashPrecisionFromZoom,
  getGeohashPrecisionFromZoomMobile,
  getOptimalZoomLevelForHybridPage,
} from 'components/search-es/geo_helper';
import type { Bounds, LatLngPoint } from 'components/search-es/types';
import { usePreferencesContext } from 'contexts/preferences';
import { createSearchApi } from 'data/search';
import { fetchAreaPolygons } from 'data/search/map/fetcher';
import type { GeoSearchFilter } from 'data/search/map/types';
import type { Filter, ListingParams, ListingParamsMethods, Sort } from 'contexts/preferences/listing-params/types';
import { MAP_PAGE_CONSTANTS, maxBoundaryPage } from 'constants/pagination';
import { IS_MOBILE_BREAKPOINT } from 'hooks/use-size-class';
import { getSortBy } from 'utils/sort';
import { getPositionFromDescription } from 'utils/google-maps/getPositionFromDescription';
import { CountryCodeList } from 'types/countries';
import type { MapViewPosition } from 'utils/redirect-to-map-view';
import { isLocaleSwitchNavigation } from 'utils/locale';
import { clearStoredMapViewport, readStoredMapViewport, writeStoredMapViewport } from '../map/map-viewport-storage';

const MAP_REFRESH_DEBOUNCE_MS = 250;
const BOUNDS_EPSILON = 1e-5;
const ZOOM_EPSILON = 0.05;
/** Count previews only read `totalCount`, so ask for the smallest page possible. */
const COUNT_PREVIEW_PAGE_SIZE = 1;

type MapWorkspaceState = {
  boundingRegion: Bounds | null;
  zoom: number | null;
};

type PaginationState = {
  page: number;
  size: number;
  count: number;
  total: number;
};

export type UseAreaPageV2WorkspaceArgs = {
  electedAddress: SingleAddress | undefined;
  listingParams: ListingParams & ListingParamsMethods;
  initialListings: readonly AreaPageListing[];
  initialPagination: PaginationState;
};

// Mobile uses a coarser geohash precision, and the desktop scale is one step
// finer than the raw zoom mapping.
function resolvePrecision(zoom: number | null, fallbackZoom: number): Precision {
  const useMobilePrecision = window.innerWidth < IS_MOBILE_BREAKPOINT;
  const adjustedZoom = useMobilePrecision ? zoom : Math.floor(zoom ?? fallbackZoom);
  const getPrecisionFn = useMobilePrecision
    ? getGeohashPrecisionFromZoomMobile
    : getGeohashPrecisionFromZoom;
  const precision = useMobilePrecision
    ? getPrecisionFn(adjustedZoom ?? fallbackZoom)
    : getPrecisionFn(adjustedZoom ?? fallbackZoom) + 1;
  return precision as Precision;
}

function boundsApproximatelyEqual(a: Bounds, b: Bounds): boolean {
  return (
    Math.abs(a.north - b.north) < BOUNDS_EPSILON &&
    Math.abs(a.south - b.south) < BOUNDS_EPSILON &&
    Math.abs(a.east - b.east) < BOUNDS_EPSILON &&
    Math.abs(a.west - b.west) < BOUNDS_EPSILON
  );
}

async function resolveMapCenter(
  address: SingleAddress,
  fallback: LatLngPoint
): Promise<LatLngPoint> {
  const addressParts = [
    address.neighbourhood,
    address.subDivision,
    address.provinceOrState,
  ].filter(Boolean) as string[];

  if (addressParts.length <= 1 && address.country) {
    addressParts.push(address.country);
  }

  const query = addressParts.join(', ') || address.country || '';
  if (!query) return fallback;

  const isUs = address.countryCode === CountryCodeList.UNITED_STATES;
  try {
    const position = await getPositionFromDescription(query, isUs);
    if (position?.latitude && position?.longitude) {
      return { lat: position.latitude, lng: position.longitude };
    }
  } catch (error) {
    console.error('AreaPageV2 geocoding error:', error);
  }
  return fallback;
}

export function useAreaPageV2Workspace({
  electedAddress,
  listingParams,
  initialListings,
  initialPagination,
}: UseAreaPageV2WorkspaceArgs) {
  const { refreshCurrentLocationFilter } = usePreferencesContext();
  const searchApi = useMemo(() => createSearchApi(), []);
  const clustersAbortController = useRef<AbortController | null>(null);
  const listingsAbortController = useRef<AbortController | null>(null);
  const countAbortController = useRef<AbortController | null>(null);
  const listingParamsRef = useRef(listingParams);
  const paginationSizeRef = useRef(initialPagination.size);
  const mapStateRef = useRef<MapWorkspaceState>({ boundingRegion: null, zoom: null });

  const [clusters, setClusters] = useState<MapPageCluster[]>([]);
  const [highlightedListingId, setHighlightedListingId] = useState<number | null>(null);
  const [isMapLoading, setIsMapLoading] = useState(false);
  const [listings, setListings] = useState<ListingCardData[]>(() =>
    initialListings.map(listing => new AreaPageListingCardData(listing))
  );
  const [pagination, setPagination] = useState<PaginationState>(initialPagination);

  useEffect(() => {
    listingParamsRef.current = listingParams;
  }, [listingParams]);

  useEffect(() => {
    paginationSizeRef.current = pagination.size;
  }, [pagination.size]);

  const optimalZoom = electedAddress ? getOptimalZoomLevelForHybridPage(electedAddress) : 11;
  const areaSlug = electedAddress?.slug?.trim().toLowerCase() ?? '';
  const isProvinceOrCountry =
    electedAddress?.addressType === AddressType.ADDRESS_TYPE_PROVINCE_OR_STATE ||
    electedAddress?.addressType === AddressType.ADDRESS_TYPE_COUNTRY;

  // Where the user left this area's map if a language change is what reloaded
  // the page. The locale switcher navigates with `window.location.href`
  // (`LocaleManager.switchToLocale`), which would otherwise reopen the map at
  // the area's optimal zoom and throw the user back out to the whole city just
  // for changing the language. Nothing else is carried over — see
  // `map-viewport-storage`.
  //
  // Read for the area this hook mounted on. The read is deliberately free of
  // side effects: it runs during render, which React repeats (StrictMode does
  // in development, and this app turns it on), and a read that also consumed
  // the entry would give the position to whichever render ran first and null to
  // the rest — the restore would quietly do nothing. `clearStoredMapViewport`
  // below is what ends the handover instead.
  //
  // Dropped for good once the area changes under the page: a soft switch gives
  // the area switched to its own framing (and the reset below its own optimal
  // zoom), and switching back must not revive this one.
  const [restoredViewport, setRestoredViewport] = useState(() => readStoredMapViewport(areaSlug));
  if (restoredViewport && restoredViewport.slug !== areaSlug) {
    setRestoredViewport(null);
  }
  const initialZoom = restoredViewport?.zoom ?? optimalZoom;

  const [mapState, setMapState] = useState<MapWorkspaceState>({
    boundingRegion: null,
    zoom: initialZoom,
  });

  useEffect(() => {
    mapStateRef.current = mapState;
  }, [mapState]);

  // One locale switch feeds one load, so the entry goes as soon as this page has
  // had its chance at it — including when it was left for another area.
  useEffect(() => {
    clearStoredMapViewport();
  }, []);

  const [mapAreaSlug, setMapAreaSlug] = useState(areaSlug);

  const filterFallbackCenter: LatLngPoint = {
    lat: listingParams.filter.latitude || 43.653226,
    lng: listingParams.filter.longitude || -79.3831843,
  };

  if (areaSlug !== mapAreaSlug) {
    setMapAreaSlug(areaSlug);
    setMapState({ boundingRegion: null, zoom: optimalZoom });
    setClusters([]);
    setListings(initialListings.map(listing => new AreaPageListingCardData(listing)));
    setPagination(initialPagination);
  }

  const {
    data: geocodedCenter = filterFallbackCenter,
    isLoading: isMapCenterPointLoading,
    isError: isMapCenterPointError,
  } = useQuery<LatLngPoint>({
    queryKey: ['area-page-v2-map-center', electedAddress?.slug],
    queryFn: () => {
      if (!electedAddress) return filterFallbackCenter;
      return resolveMapCenter(electedAddress, filterFallbackCenter);
    },
    // A restored viewport already says where to open, so the area's name is not
    // geocoded at all — the map would only be moved off the restored position.
    enabled: Boolean(electedAddress?.slug) && !restoredViewport,
    staleTime: 5 * 60 * 1000,
  });

  const mapCenter = restoredViewport?.center ?? geocodedCenter;

  const { data: areaPolygonWkt = '' } = useQuery<string>({
    queryKey: ['area-page-v2-polygon', electedAddress?.slug],
    queryFn: async () => {
      if (!electedAddress?.slug) return '';
      const response = await fetchAreaPolygons({ slug: electedAddress.slug });
      return response.data?.[0]?.polygon ?? '';
    },
    enabled: Boolean(electedAddress?.slug) && !isProvinceOrCountry,
    staleTime: 5 * 60 * 1000,
  });

  const fetchClusters = useCallback(async (slug: string, zoom: number, bounds?: Bounds) => {
    setIsMapLoading(true);
    clustersAbortController.current?.abort();
    clustersAbortController.current = new AbortController();
    const params = listingParamsRef.current;
    const filter: GeoSearchFilter = { ...cloneDeep(params.filter) };
    let nextClusters: MapPageCluster[] = [];
    try {
      const useMobilePrecision = window.innerWidth < IS_MOBILE_BREAKPOINT;
      const precision = useMobilePrecision
        ? getGeohashPrecisionFromZoomMobile(zoom)
        : getAreaClusterPrecisionFromZoom(zoom);
      const response = await searchApi.searchClustersWithinArea(
        slug,
        precision as Precision,
        filter,
        getSortBy(params.sort),
        clustersAbortController.current.signal,
        MAP_PAGE_CONSTANTS.DEFAULT_CLUSTER_DATA_SIZE,
        bounds
      );
      nextClusters = response.clusters;
    } catch (error: unknown) {
      const err = error as { name?: string } | undefined;
      if (err?.name !== 'AbortError') {
        console.error(error);
      }
      nextClusters = [];
    } finally {
      clustersAbortController.current = null;
      setIsMapLoading(false);
    }

    setClusters(prev => {
      if (prev.length === nextClusters.length && prev.every((item, i) => item.key === nextClusters[i].key)) {
        return prev;
      }
      return nextClusters;
    });
  }, [searchApi]);

  const fetchListings = useCallback(async (
    bounds: Bounds | null,
    zoom: number | null,
    pageNumber: number
  ) => {
    if (!areaSlug.trim()) return;
    listingsAbortController.current?.abort();
    listingsAbortController.current = new AbortController();
    const params = listingParamsRef.current;
    try {
      const precision = resolvePrecision(zoom, optimalZoom);
      const filter: GeoSearchFilter = { ...cloneDeep(params.filter) };
      const response = await searchApi.searchListingsInBoundary(
        bounds,
        precision as Precision,
        String(pageNumber),
        filter,
        getSortBy(params.sort),
        listingsAbortController.current.signal,
        paginationSizeRef.current,
        areaSlug,
        'offset'
      );
      const nextListings = response.listings.map(listing => new AreaPageListingCardData(listing));
      setListings(prev => {
        if (prev.length === nextListings.length && prev.every((item, i) => item.id === nextListings[i].id)) {
          return prev;
        }
        return nextListings;
      });
      setPagination(prev => ({
        page: pageNumber,
        size: prev.size,
        count: response.totalCount,
        total: Math.min(response.totalPages ?? 1, maxBoundaryPage(prev.size)),
      }));
    } catch (error: unknown) {
      const err = error as { name?: string } | undefined;
      if (err?.name !== 'AbortError') {
        console.error(error);
      }
    } finally {
      listingsAbortController.current = null;
    }
  }, [areaSlug, optimalZoom, searchApi]);

  /**
   * How many listings the search would return if `patch` were applied.
   *
   * Lets a surface preview a filter change before committing to it. Only the
   * total is used, so it asks for a single-listing page. Returns null when the
   * request is superseded or fails — callers keep showing their last good
   * count rather than flashing a wrong one.
   *
   * `targetAreaSlug` previews another area instead of this one, for a change
   * that would move the page as well as filter it. That count is area-wide:
   * the map bounds describe a viewport over the current area and would silently
   * clip a different one to the wrong part of the map.
   */
  const countListingsForFilter = useCallback(async (
    patch: PartialDeep<Filter>,
    targetAreaSlug?: string
  ): Promise<number | null> => {
    const slug = targetAreaSlug?.trim() || areaSlug.trim();
    if (!slug) return null;
    countAbortController.current?.abort();
    const controller = new AbortController();
    countAbortController.current = controller;
    const params = listingParamsRef.current;
    const isCurrentArea = slug === areaSlug.trim();
    const { boundingRegion, zoom } = isCurrentArea
      ? mapStateRef.current
      : { boundingRegion: null, zoom: null };
    try {
      const merged = deepmerge(params.filter, patch as Partial<Filter>, {
        arrayMerge: (_target, source) => source,
      }) as Filter;
      const filter: GeoSearchFilter = { ...cloneDeep(merged) };
      const response = await searchApi.searchListingsInBoundary(
        boundingRegion,
        resolvePrecision(zoom, optimalZoom),
        '',
        filter,
        getSortBy(params.sort),
        controller.signal,
        COUNT_PREVIEW_PAGE_SIZE,
        slug
      );
      return response.totalCount;
    } catch (error: unknown) {
      const err = error as { name?: string } | undefined;
      if (err?.name !== 'AbortError') {
        console.error(error);
      }
      return null;
    } finally {
      if (countAbortController.current === controller) {
        countAbortController.current = null;
      }
    }
  }, [areaSlug, optimalZoom, searchApi]);

  const refreshWorkspace = useCallback(() => {
    const { boundingRegion, zoom } = mapStateRef.current;
    if (!boundingRegion || zoom == null || !areaSlug.trim() || isProvinceOrCountry) {
      return;
    }
    void fetchClusters(areaSlug, zoom, boundingRegion);
    setPagination(prev => (prev.page === 1 ? prev : { ...prev, page: 1 }));
    void fetchListings(boundingRegion, zoom, 1);
  }, [areaSlug, fetchClusters, fetchListings, isProvinceOrCountry]);

  const refreshWorkspaceRef = useRef(refreshWorkspace);
  useEffect(() => {
    refreshWorkspaceRef.current = refreshWorkspace;
  }, [refreshWorkspace]);

  /**
   * Refetch after the search itself changed — a filter or sort edit.
   *
   * Unlike {@link refreshWorkspace}, this does not wait on the map. Listings
   * are scoped by area slug and search fine with no bounds, so the results have
   * to update even when the map has never reported a region: it is unmounted
   * behind the full-screen listings view, hidden on mobile, or failed to
   * initialise. Clusters are the part that genuinely needs bounds, so they stay
   * gated and the stale set is dropped either way.
   */
  const refreshForSearchChange = useCallback(() => {
    if (!areaSlug.trim()) return;
    const { boundingRegion, zoom } = mapStateRef.current;
    setPagination(prev => (prev.page === 1 ? prev : { ...prev, page: 1 }));
    void fetchListings(boundingRegion, zoom, 1);
    if (boundingRegion && zoom != null && !isProvinceOrCountry) {
      void fetchClusters(areaSlug, zoom, boundingRegion);
    }
  }, [areaSlug, fetchClusters, fetchListings, isProvinceOrCountry]);

  const refreshForSearchChangeRef = useRef(refreshForSearchChange);
  useEffect(() => {
    refreshForSearchChangeRef.current = refreshForSearchChange;
  }, [refreshForSearchChange]);

  // When the area changes on the client WITHOUT a fresh SSR fetch — a soft switch,
  // e.g. applying a saved search for another area — load the new area's listings AND
  // clusters/pins by slug (no bounds), mirroring the server's initial fetch. On a real
  // navigation the SSR `initialListings` change and the reconciliation above already
  // handle it, so this is skipped. Area-wide clusters are needed because a no-polygon
  // area never fires the map region-change that would otherwise refresh them; the map
  // refines both once it settles on the new region.
  const prevAreaSlugRef = useRef(areaSlug);
  const prevInitialListingsRef = useRef(initialListings);
  useEffect(() => {
    const areaChanged = prevAreaSlugRef.current !== areaSlug;
    const ssrListingsChanged = prevInitialListingsRef.current !== initialListings;
    prevAreaSlugRef.current = areaSlug;
    prevInitialListingsRef.current = initialListings;
    if (!areaChanged || ssrListingsChanged) return;
    if (!areaSlug.trim() || isProvinceOrCountry) return;
    // Deferred so the fetches (which set state) run outside the effect body.
    const timeoutId = window.setTimeout(() => {
      void fetchListings(null, optimalZoom, 1);
      void fetchClusters(areaSlug, optimalZoom);
    }, 0);
    return () => window.clearTimeout(timeoutId);
  }, [areaSlug, initialListings, isProvinceOrCountry, optimalZoom, fetchListings, fetchClusters]);

  // Hand the live map position to the load a language change is about to cause,
  // so the map reopens where the user left it rather than back at the area's
  // optimal zoom. `pagehide` because only an unload loses the position, and the
  // locale-switch check because only that one reload should keep it: a refresh
  // records nothing and re-frames on the area, as a reload of an area page is
  // expected to (`isLocaleSwitchNavigation` is set on the way out, so it is
  // already true by the time this runs).
  useEffect(() => {
    if (!areaSlug.trim()) return;
    const recordViewport = () => {
      if (!isLocaleSwitchNavigation()) return;
      const { boundingRegion, zoom } = mapStateRef.current;
      if (!boundingRegion || zoom == null) return;
      writeStoredMapViewport({
        slug: areaSlug,
        center: calculateCenterPoint(boundingRegion),
        zoom,
      });
    };
    window.addEventListener('pagehide', recordViewport);
    return () => window.removeEventListener('pagehide', recordViewport);
  }, [areaSlug]);

  // reload listings/pins when the user pans or zooms the map
  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      refreshWorkspaceRef.current();
    }, MAP_REFRESH_DEBOUNCE_MS);
    return () => window.clearTimeout(timeoutId);
  }, [mapState.boundingRegion, mapState.zoom]);

  const onMapBoundingRegionChanged = useCallback((_oldBounds: Bounds | null, newBounds: Bounds) => {
    if (!areMapBoundsUsableForSearch(newBounds)) return;
    setMapState(prev => {
      if (prev.boundingRegion && boundsApproximatelyEqual(prev.boundingRegion, newBounds)) {
        return prev;
      }
      return { ...prev, boundingRegion: newBounds };
    });
  }, []);

  const onMapZoomChanged = useCallback((_oldZoom: number, newZoom: number) => {
    if (!Number.isFinite(newZoom) || newZoom <= 0) return;
    setMapState(prev => {
      if (prev.zoom != null && Math.abs(prev.zoom - newZoom) < ZOOM_EPSILON) {
        return prev;
      }
      return { ...prev, zoom: newZoom };
    });
  }, []);

  const onListingHover = useCallback((listingId: number | null) => {
    setHighlightedListingId(listingId);
  }, []);

  // The current, live map position (as opposed to `mapCenter`/`mapZoom` above, which
  // are the area's resolved/optimal values) — used by "Remove Boundary" to preserve
  // wherever the user has panned to rather than jumping back to the area's center.
  const getCurrentPosition = useCallback((): MapViewPosition | undefined => {
    const { boundingRegion, zoom } = mapStateRef.current;
    if (!boundingRegion || zoom == null) return undefined;
    const center = calculateCenterPoint(boundingRegion);
    return { latitude: center.lat, longitude: center.lng, zoom: Math.round(zoom) };
  }, []);

  const applyFilters = useCallback((filter: Filter) => {
    const params = listingParamsRef.current;
    const next = deepmerge(params.filter, filter, { arrayMerge: (_target, source) => source }) as Filter;
    params.setFilter(next);
    refreshCurrentLocationFilter(params.filter);
    setClusters([]);
    refreshForSearchChangeRef.current();
  }, [refreshCurrentLocationFilter]);

  const applySort = useCallback((sort: Sort) => {
    const params = listingParamsRef.current;
    if (params.sort === sort) return;
    params.setSort(sort);
    refreshForSearchChangeRef.current();
  }, []);

  const setPageNumber = useCallback(async (pageNumber: number) => {
    if (!Number.isFinite(pageNumber) || pageNumber < 1) return;
    const clamped = Math.min(pageNumber, maxBoundaryPage(paginationSizeRef.current));
    setPagination(prev => (prev.page === clamped ? prev : { ...prev, page: clamped }));
    await fetchListings(mapStateRef.current.boundingRegion, mapStateRef.current.zoom, clamped);
  }, [fetchListings]);

  return {
    clusters,
    highlightedListingId,
    isMapLoading,
    areaPolygonWkt: isProvinceOrCountry ? undefined : areaPolygonWkt,
    mapCenter,
    mapZoom: initialZoom,
    isMapCenterPointLoading,
    isMapCenterPointError,
    listings,
    pagination,
    onMapBoundingRegionChanged,
    onMapZoomChanged,
    onListingHover,
    applyFilters,
    applySort,
    countListingsForFilter,
    refreshWorkspace,
    setPageNumber,
    getCurrentPosition,
  };
}
